/* eslint-disable max-len */
import { Col, Card, Select, Row } from 'antd';
import { useFormikContext } from 'formik';
import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { type MobilityModuleSpecsFragment } from '../../../../api/fragments';
import {
    ApplicationScenario,
    DateTimeUnit,
    type MobilityModuleUpdateSettings,
    ModuleRole,
} from '../../../../api/types';
import CascaderField from '../../../../components/fields/CascaderField';
import DealershipTranslatedMarkdownField from '../../../../components/fields/DealershipFields/DealershipTranslatedMarkdown';
import DealershipTranslatedTextAreaField from '../../../../components/fields/DealershipFields/DealershipTranslatedTextArea';
import InputField from '../../../../components/fields/InputField';
import InputNumberField from '../../../../components/fields/InputNumberField';
import ModuleSelectField from '../../../../components/fields/ModuleSelectField';
import SelectField from '../../../../components/fields/SelectField';
import SwitchField from '../../../../components/fields/SwitchField';
import useExternalModelInfoTooltip from '../../../../utilities/useExternalModelInfoTooltip';
import useSystemOptions from '../../../../utilities/useSystemOptions';
import useSystemSwitchData from '../../../../utilities/useSystemSwitchData';
import useTooltip from '../../../../utilities/useTooltip';
import { hasPaymentScenario } from '../modules/implementations/shared';
import LocationFieldArray from '../modules/implementations/shared/LocationFieldArray';
import UnavailableTimeRangeFieldArray from '../modules/implementations/shared/UnavailableTimeRange';
import useModuleOptions from '../modules/implementations/shared/useModuleOptions';

export type FormValues = MobilityModuleUpdateSettings;
const { Option } = Select;

type MobilityModuleDetailsFormProps = {
    module: MobilityModuleSpecsFragment;
};

const MobilityModuleDetailsForm = ({ module }: MobilityModuleDetailsFormProps) => {
    const { t } = useTranslation(['moduleDetails', 'mobilityModuleDetails', 'common']);

    const options = useModuleOptions(module.company, {
        addNoneOptions: { promoCodes: true, payment: true, giftVouchers: true },
    });
    const { yesNoSwitch } = useSystemSwitchData();
    const { values, setFieldValue, initialValues } = useFormikContext<MobilityModuleUpdateSettings>();
    const { dayOfWeek } = useSystemOptions();
    const externalModelInfoTooltip = useExternalModelInfoTooltip();
    const rentalRequirementToolTip = useTooltip('rentalRequirement');
    const rentalDisclaimerToolTip = useTooltip('rentalDisclaimer');
    const { scenarios, signing } = values;
    const hasPaymentField = useMemo(() => hasPaymentScenario(scenarios), [scenarios]);
    const scenarioOptions = useMemo(
        () => [
            {
                value: ApplicationScenario.Booking,
                label: t('moduleDetails:explanations.scenarios.Booking.label'),
                disabled: true,
            },
            {
                value: ApplicationScenario.Payment,
                label: t('moduleDetails:explanations.scenarios.Payment.label'),
            },
        ],
        [t]
    );

    useEffect(() => {
        if (!values.sendEmailToCustomer) {
            setFieldValue('sendPDFToCustomer', false);
        }
    }, [setFieldValue, values.sendEmailToCustomer]);

    return (
        <Card>
            <Row gutter={10}>
                <Col md={8} xs={24}>
                    <InputField
                        {...t('moduleDetails:fields.displayName', { returnObjects: true })}
                        name="displayName"
                        required
                    />
                </Col>
                <Col md={8} xs={24}>
                    <SelectField
                        {...t('moduleDetails:fields.applicationScenario', { returnObjects: true })}
                        mode="multiple"
                        name="scenarios"
                        options={scenarioOptions}
                        required
                        showSearch
                    />
                </Col>
                <Col md={8} xs={24}>
                    <SelectField
                        {...t('moduleDetails:fields.promoCodeModule', { returnObjects: true })}
                        name="promoCodeModuleId"
                        options={options.promoCodes}
                        showSearch
                    />
                </Col>
                <Col md={8} xs={24}>
                    <SelectField
                        {...t('moduleDetails:fields.giftVoucherModule', { returnObjects: true })}
                        name="giftVoucherModuleId"
                        options={options.giftVouchers}
                        showSearch
                    />
                </Col>
                <Col md={8} xs={24}>
                    <InputNumberField
                        {...t('mobilityModuleDetails:fields.minimumAdvancedBooking', { returnObjects: true })}
                        addonAfter={t('mobilityModuleDetails:suffix.daysBefore')}
                        name="minimumAdvancedBooking"
                        required
                    />
                </Col>
                <Col md={8} xs={24}>
                    <InputNumberField
                        {...t('mobilityModuleDetails:fields.durationBeforeNextBooking', { returnObjects: true })}
                        addonAfter={t('mobilityModuleDetails:suffix.hours')}
                        name="durationBeforeNextBooking"
                        required
                    />
                </Col>
                <Col md={8} xs={24}>
                    <InputNumberField
                        {...t('mobilityModuleDetails:fields.amendmentCutOff', { returnObjects: true })}
                        addonAfter={t('mobilityModuleDetails:suffix.daysBefore')}
                        name="amendmentCutOff"
                        required
                    />
                </Col>
                <Col md={8} xs={24}>
                    <DealershipTranslatedTextAreaField
                        {...t('mobilityModuleDetails:fields.rentalDisclaimer', { returnObjects: true })}
                        name="rentalDisclaimer"
                        tooltip={rentalDisclaimerToolTip}
                    />
                </Col>
                <Col md={8} xs={24}>
                    <DealershipTranslatedMarkdownField
                        {...t('mobilityModuleDetails:fields.rentalRequirement', { returnObjects: true })}
                        name="rentalRequirement"
                        tooltip={rentalRequirementToolTip}
                    />
                </Col>
                <Col lg={8} xs={24}>
                    <SwitchField
                        {...t('mobilityModuleDetails:fields.externalModelInfo', { returnObjects: true })}
                        {...yesNoSwitch}
                        name="externalModelInfo"
                        tooltip={externalModelInfoTooltip}
                    />
                </Col>
                {values.externalModelInfo && (
                    <Col md={8} xs={24}>
                        <InputField
                            {...t('mobilityModuleDetails:fields.baseUrl', { returnObjects: true })}
                            addonBefore="https://"
                            name="baseUrl"
                            required
                        />
                    </Col>
                )}
                <Col lg={8} xs={24}>
                    <SwitchField
                        {...t('mobilityModuleDetails:fields.persistKYCData', { returnObjects: true })}
                        {...yesNoSwitch}
                        name="persistKYCData"
                    />
                </Col>
                <Col md={8} xs={24}>
                    <SwitchField
                        {...yesNoSwitch}
                        {...t('moduleDetails:fields.showResetKYCButton', { returnObjects: true })}
                        name="showResetKYCButton"
                    />
                </Col>
                {hasPaymentField && (
                    <Col md={8} xs={24}>
                        <CascaderField
                            {...t('moduleDetails:fields.paymentModuleId', { returnObjects: true })}
                            name="paymentSettingsId"
                            options={options.payment}
                            showSearch
                        />
                    </Col>
                )}
                <Col md={8} xs={24}>
                    <CascaderField
                        {...t('moduleDetails:fields.liveChatSetting', { returnObjects: true })}
                        name="liveChatSettingId"
                        options={options.liveChat}
                        showSearch
                    />
                </Col>
                <Col md={8} xs={24}>
                    <SelectField
                        {...t('moduleDetails:fields.unavailableDayofWeek', { returnObjects: true })}
                        mode="multiple"
                        name="unavailableDayOfWeek"
                        options={dayOfWeek}
                        showSearch
                    />
                </Col>
                <Col md={8} xs={24}>
                    <InputNumberField
                        {...t('mobilityModuleDetails:fields.availableNumberOfBookingRange', { returnObjects: true })}
                        addonAfter={
                            <Select
                                defaultValue={initialValues.availableNumberOfBookingRange.unit}
                                onChange={value => setFieldValue('availableNumberOfBookingRange.unit', value)}
                                value={values.availableNumberOfBookingRange.unit}
                            >
                                <Option value={DateTimeUnit.Days}>{t('common:options.dateTimeUnit.days')}</Option>
                                <Option value={DateTimeUnit.Hours}>{t('common:options.dateTimeUnit.hours')}</Option>
                            </Select>
                        }
                        name="availableNumberOfBookingRange.value"
                        required
                    />
                </Col>
                <Col md={8} xs={24}>
                    <SwitchField
                        {...t('mobilityModuleDetails:fields.signing.isEnabled', { returnObjects: true })}
                        name="signing.isEnabled"
                        {...yesNoSwitch}
                        required
                    />
                </Col>
                {signing.isEnabled && (
                    <Col md={8} xs={24}>
                        <ModuleSelectField
                            {...t(`mobilityModuleDetails:fields.signing.module`, { returnObjects: true })}
                            companyId={module.companyId}
                            moduleRole={ModuleRole.Signing}
                            name="signing.moduleId"
                            addNoneOption
                        />
                    </Col>
                )}
                <Col md={8} xs={24}>
                    <SwitchField
                        {...t('mobilityModuleDetails:fields.sendEmailToCustomer', { returnObjects: true })}
                        name="sendEmailToCustomer"
                        {...yesNoSwitch}
                        required
                    />
                </Col>
                {values.sendEmailToCustomer && (
                    <Col md={8} xs={24}>
                        <SwitchField
                            {...t('mobilityModuleDetails:fields.sendPDFToCustomer', { returnObjects: true })}
                            name="sendPDFToCustomer"
                            {...yesNoSwitch}
                            required
                        />
                    </Col>
                )}
                <Col span={24}>
                    <LocationFieldArray name="locations" />
                </Col>
                <Col span={24}>
                    <UnavailableTimeRangeFieldArray name="unavailableTimeRange" />
                </Col>
            </Row>
        </Card>
    );
};

export default MobilityModuleDetailsForm;
